"use client";

import type { CSSProperties } from "react";
import Image from "next/image";

import type { PageRizAffiliateSchema } from "@/sanity/pages/PageRizAffiliateTemplate";
import { cn } from "@/lib/utils";
import ChevronCta from "./ChevronCta";
import { CountUp, RevealBlock, RevealEyebrow, RevealStyles, RevealTitle } from "./edge/reveal";

import texture from "@/public/riz-affiliate/vip-texture.jpg";

const notches = (centres: number[], height: number, radius: string, x: string) =>
  centres
    .map((y) => `radial-gradient(circle ${radius} at ${x} ${((y / height) * 100).toFixed(3)}%, transparent 99%, #000 100%)`)
    .join(",");

const TICKET_MASK = {
  "--tk-m": notches(
    [20.292, 46.037, 71.783, 97.529, 123.274, 149.021, 174.767, 200.513, 226.258],
    241,
    "1.2814rem",
    "-0.0074rem",
  ),
  "--tk-d": notches(
    [16.523, 37.12, 57.717, 78.314, 98.91, 119.507, 140.104, 160.699, 181.296, 201.893, 222.489, 243.086],
    252.8,
    "1.0168rem",
    "-0.1585rem",
  ),
  WebkitMaskImage: "var(--tk)",
  maskImage: "var(--tk)",
  WebkitMaskComposite: "source-in",
  maskComposite: "intersect",
} as CSSProperties;

const CARD_LAYOUT = [
  { card: "lg:w-[37.9035rem]", shape: "", box: "h-full px-[3.7546rem] pb-[5.721rem] lg:pl-[3.0036rem]", inner: "items-start", row: "items-center justify-center", labelRow: "", label: "" },
  { card: "lg:w-[37.9751rem]", shape: "lg:left-[0.036rem]", box: "h-[29.625rem] px-[4rem] pb-[5.721rem] lg:pl-[6.4365rem]", inner: "items-center justify-center lg:items-start", row: "items-end", labelRow: "lg:pl-[1.2rem]", label: "w-[25.25rem]" },
  { card: "lg:w-[37.9751rem]", shape: "lg:left-[0.035rem]", box: "h-full px-[4rem] pb-[3.0394rem] lg:pl-[6.4365rem]", inner: "items-start lg:items-start", row: "items-end", labelRow: "lg:pl-[1.2rem]", label: "" },
];

export default function VipDealSection({ data }: { data: PageRizAffiliateSchema }) {
  if (!data?.vipDealTitle?.length && !data?.vipDealItems?.length && !data?.vipDealCTA) return null;

  return (
    <section className="relative overflow-hidden bg-[#050B09] py-[8rem] lg:py-[13.75rem] [&_*]:antialiased">
      <RevealStyles />
      <div
        aria-hidden
        className="pointer-events-none absolute left-[-17.5163rem] top-[3.0125rem] flex opacity-[0.09] mix-blend-exclusion lg:left-0 lg:top-0">
        {[0, 1].map((tile) => (
          <Image
            key={tile}
            src={texture}
            alt=""
            sizes="(min-width: 993px) 62vw, 272vw"
            className="!h-auto !w-[148rem] max-w-none shrink-0"
          />
        ))}
      </div>

      <div className="relative mx-auto flex w-full flex-col items-center gap-[4rem] px-[2rem] lg:w-[min(150rem,100%)] lg:px-0">
        {(data?.vipDealCalloutText || data?.vipDealTitle?.length > 0) && (
          <div className="flex w-full flex-col items-center gap-[2rem] lg:gap-[1rem]">
            <RevealEyebrow text={data?.vipDealCalloutText} />
            <RevealTitle
              value={data?.vipDealTitle}
              className="richtext richtext-center w-full pb-[0.39rem] pr-[0.49rem] pt-[0.539rem] [text-wrap:wrap] prose-h2:m-0 prose-h2:font-geist prose-h2:text-[4.75rem] prose-h2:font-[500] prose-h2:leading-[1.15] prose-h2:tracking-[-0.2375rem] prose-h2:text-white lg:pb-[0.764rem] lg:pr-[0.3rem] lg:pt-[0.336rem] lg:prose-h2:text-[6rem] lg:prose-h2:tracking-[-0.3rem] [--hl-ctr:0px] [--hl-dy:0rem] [--hl-ext:0rem] [--hl-ml:0.25em] [--hl-mr:0.192em] [--hl-pl:0.425rem] [--hl-pr:0.9625rem] [--hl-tracking:0em] [--hl-wash:5.5rem] lg:[--hl-dy:0.1875rem] lg:[--hl-ext:-0.5rem] lg:[--hl-ml:0.1927em] lg:[--hl-mr:0.1927em] lg:[--hl-pl:0.7375rem] lg:[--hl-pr:0.9rem] lg:[--hl-wash:6.75rem]"
            />
          </div>
        )}

        {data?.vipDealItems?.length > 0 && (
          <div className="flex w-full flex-col items-center gap-[3rem] lg:flex-row lg:justify-center lg:gap-[4rem] lg:py-[1.5125rem]">
            {data.vipDealItems.map((item, i) => {
              const layout = CARD_LAYOUT[Math.min(i, CARD_LAYOUT.length - 1)];
              return (
                <RevealBlock
                  key={`${item?.label ?? "item"}-${i}`}
                  index={i}
                  className={cn("relative h-[30.25rem] w-full lg:h-[31.6rem]", layout.card)}>
                  <div
                    aria-hidden
                    className={cn("absolute left-0 top-[0.125rem] h-[30.125rem] w-full border border-white/[0.04] bg-white/[0.03] backdrop-blur-[1.14px] [--tk:var(--tk-m)] lg:top-0 lg:h-full lg:w-[37.832rem] lg:[--tk:var(--tk-d)]", layout.shape)}
                    style={TICKET_MASK}
                  />
                  <div
                    aria-hidden
                    className="absolute left-[-1.0563rem] top-0 w-[47.29rem] lg:left-0 lg:w-[37.832rem]">
                    <div className={cn("absolute left-0 top-0 h-[0.134rem] bg-[#75D4B9] lg:h-px", i === 0 ? "w-[87.15%]" : "w-[48.39%]")} />
                    <div className="mt-[0.134rem] h-[0.134rem] bg-white/[0.12] lg:mt-0 lg:h-px" />
                  </div>

                  <div
                    className={cn(
                      "absolute left-[-1.0725rem] top-0 flex w-full flex-col items-center justify-center pt-[5.721rem] lg:left-0 lg:h-full lg:pb-[2.4315rem] lg:pr-[3.0036rem] lg:pt-[4.577rem]",
                      layout.box,
                    )}>
                    <div className={cn("flex shrink-0 flex-col gap-[3.129rem] lg:items-center lg:gap-[2rem]", layout.inner)}>
                      <p
                        className={cn(
                          "m-0 flex w-full shrink-0 gap-[0.0894rem] whitespace-nowrap font-geist font-[500] lg:w-auto lg:items-end lg:justify-center lg:gap-[0.0715rem]",
                          layout.row,
                        )}>
                        {item?.value && (
                          <CountUp
                            value={item.value}
                            className="bg-[linear-gradient(180deg,#D6EEE7_35.393%,rgba(255,255,255,0)_166.01%)] bg-clip-text text-[15rem] leading-[1.2] tracking-[-0.9rem] text-transparent lg:-my-[1rem] lg:bg-[linear-gradient(180deg,#D6EEE7_38.45%,rgba(255,255,255,0)_162.1%)] lg:py-[1rem] lg:text-[16rem] lg:tracking-[-0.96rem] lg:[&>span]:[text-box-edge:cap_alphabetic] lg:[&>span]:[text-box-trim:trim-both]"
                          />
                        )}
                        {item?.unit && (
                          <span className="ml-[0.9rem] mr-[0.5rem] text-[10rem] leading-[1.2] tracking-[-0.5rem] text-white/[0.36] lg:ml-[0.96rem] lg:mr-[0.3rem] lg:text-[6rem] lg:tracking-[-0.3rem] lg:[text-box-edge:cap_alphabetic] lg:[text-box-trim:trim-both]">
                            {item.unit}
                          </span>
                        )}
                      </p>
                      {item?.label && (
                        <div className={cn("flex w-full shrink-0 items-center justify-center pl-[2.1455rem] lg:w-auto lg:pl-0", layout.labelRow)}>
                          <p
                            className={cn(
                              "m-0 text-center font-geist text-[2.25rem] font-[400] leading-[1.33] text-[#D6EEE7]/[0.9] lg:w-auto lg:text-left lg:text-[2rem]",
                              layout.label,
                            )}>
                            {item.label}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </RevealBlock>
              );
            })}
          </div>
        )}

        {data?.vipDealCTA && (
          <RevealBlock className="flex w-full justify-center">
            <ChevronCta
              data={data.vipDealCTA}
              variant="white"
              language={data?.settings?.language}
            />
          </RevealBlock>
        )}
      </div>
    </section>
  );
}
